import { useEffect, useContext } from "react";
import { contexto } from "./CustomProvider";
import { updateUser } from "./ApiHandlers";
import {
    isMobile,
    getBrowserType,
    getSOFromDevice,
    getTableKeysLocalStorage,
    updateTableKeysLocalStorage
} from "./utils";

const DeviceManager = () => {
    const { usuarioContexto, setUsuarioContexto } = useContext(contexto);

    const generateNewDeviceID = () => {
        const d = new Date().getTime();
        const n = parseInt(Math.random() * 100000);
        return d + "_" + n;
    };

    const getOrCreateDeviceId = () => {
        const storedId = getTableKeysLocalStorage("deviceID");
        if (storedId) {
            return storedId;
        } else {
            const newID = generateNewDeviceID();
            updateTableKeysLocalStorage("deviceID", newID);
            return newID;
        }
    };

    const isRunningAsPWA = () => {
        const standaloneMatchMedia = window.matchMedia('(display-mode: standalone)').matches;
        const standaloneIOS = window.navigator.standalone === true;
        return standaloneMatchMedia || standaloneIOS;
    };

    const getDeviceInfo = () => {
        try {
            return {
                screenWidth: window.screen.width,
                screenHeight: window.screen.height,
                userAgent: navigator.userAgent,
                isMobile: isMobile(),
                browser: getBrowserType(),
                os: getSOFromDevice(),
                runningMode: isRunningAsPWA() ? "pwa" : "browser",
                platform: navigator.platform || "unknown",
                language: navigator.language || "unknown",
                lastUpdated: new Date().toISOString()
            };
        } catch (error) {
            console.error("❌ Error obteniendo información del dispositivo:", error);
            return {
                screenWidth: 0,
                screenHeight: 0,
                userAgent: "unknown",
                lastUpdated: new Date().toISOString()
            };
        }
    };

    // Función para comparar si hay cambios significativos
    const hasSignificantChanges = (oldDevice, newInfo) => {
        if (!oldDevice) return true; // Dispositivo nuevo

        // Comparar solo propiedades relevantes (excluir lastUpdated)
        const relevantProps = [
            'screenWidth', 'screenHeight', 'userAgent', 'isMobile',
            'browser', 'os', 'runningMode', 'platform', 'language'
        ];

        return relevantProps.some(prop => oldDevice[prop] !== newInfo[prop]);
    };

    const initializeDevice = () => {
        const deviceId = getOrCreateDeviceId();
        const deviceInfo = getDeviceInfo();

        // Si no hay usuario logueado, solo guardamos el deviceId
        if (!usuarioContexto?.uid) {
            console.log("⚠️ No hay usuario logueado, solo guardando deviceId");
            return;
        }

        const dispositivos = usuarioContexto.dispositivos || [];
        const existingDeviceIndex = dispositivos.findIndex(d => d.deviceId === deviceId);
        const existingDevice = existingDeviceIndex !== -1 ? dispositivos[existingDeviceIndex] : null;

        // Verificar si hay cambios significativos
        const needsUpdate = hasSignificantChanges(existingDevice, deviceInfo);

        if (!needsUpdate && existingDevice) {
            return;
        }

        let updatedDispositivos;
        if (existingDeviceIndex !== -1) {
            // Actualizar dispositivo existente - PRESERVAR todas las propiedades existentes
            updatedDispositivos = dispositivos.map((device, idx) => {
                if (idx === existingDeviceIndex) {
                    return {
                        ...device,              // Mantener TODAS las propiedades existentes primero
                        ...deviceInfo           // Sobrescribir solo las del deviceInfo
                    };
                }
                return device;
            });
            console.log("🔄 Actualizando dispositivo existente");
        } else {
            // Crear nuevo dispositivo
            updatedDispositivos = [
                ...dispositivos,
                {
                    deviceId,
                    ...deviceInfo,
                    location: null,
                    locationStatus: null,
                    config: {},
                    registeredAt: new Date().toISOString()
                }
            ];
        }

        // Actualizar en context y backend
        const updatedUser = {
            ...usuarioContexto,
            dispositivos: updatedDispositivos
        };

        setUsuarioContexto(updatedUser);
        updateTableKeysLocalStorage("user", updatedUser);

        // Solo enviar al backend cuando hay cambios reales
        updateUser(usuarioContexto.uid, { dispositivos: updatedDispositivos })
    };

    useEffect(() => {
        // Solo inicializar si hay usuario logueado
        if (usuarioContexto?.uid) {
            initializeDevice();
        }
    }, [usuarioContexto?.uid]);

    return null;
};

export default DeviceManager;